import { NgIf, NgFor, AsyncPipe, DatePipe } from '@angular/common';
import { Component, Inject } from '@angular/core';

import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  MatNavList,
  MatListItem,
  MatListItemAvatar,
  MatListItemTitle,
  MatListItemLine,
  MatListItemMeta,
  MatDivider,
} from '@angular/material/list';
import { Router } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { PageToolbarComponent } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { WrapperSiteSidenavActions } from '@next/core-lib/wrapper-site-sidenav';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, skipWhile, take } from 'rxjs/operators';

import { getFeedAdvices } from '../../../feed-advice/store/feed-advice.selectors';
import { FeedAdviceApplication } from '../../models/animal-group.model';
import * as animalGroupActions from '../../store/animal-group.actions';

import { getSelectedAnimalGroup } from '../../store/animal-group.selectors';
import { FeedadviceApplicationItemDialogComponent } from '../feedadvice-application-item-dialog/feedadvice-application-item-dialog.component';

@Component({
  selector: 'app-feedadvice-applications',
  templateUrl: './feedadvice-applications.component.html',
  imports: [
    PageToolbarComponent,
    NgIf,
    MatNavList,
    NgFor,
    MatListItem,
    MatIcon,
    MatListItemAvatar,
    MatListItemTitle,
    MatListItemLine,
    MatListItemMeta,
    MatIconButton,
    MatDivider,
    AsyncPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class FeedadviceApplicationsComponent {
  animalGroup$ = this.store.select(getSelectedAnimalGroup);
  feedadvices$ = this.store.select(getFeedAdvices);

  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  constructor(
    private readonly store: Store<RootState>,
    private readonly router: Router,
    @Inject(BreakpointServiceInjector)
    private readonly breakpoint: BreakpointService,
    private readonly dialog: DialogService,
    private readonly localeService: LocaleService,
  ) {}

  getFeedadviceName(feedadvice: FeedAdviceApplication): Observable<string> {
    return this.feedadvices$.pipe(
      take(1),
      map(
        (feedadvices) =>
          !!feedadvices && feedadvices.find((fa) => fa.id === feedadvice.feedAdviceId)?.name,
      ),
    );
  }

  add() {
    this.animalGroup$.pipe(take(1)).subscribe((animalGroup) => {
      this.router.navigate(['/animalGroup', animalGroup.id, 'feedAdvice-applications', 'add']);
      this.breakpoint.ltLg$.pipe(take(1)).subscribe((isMobile) => {
        if (isMobile) {
          this.store.dispatch(WrapperSiteSidenavActions.closeSidenav());
        }
      });
    });
  }

  addFeedAdviceApplication() {
    this.store.dispatch(
      animalGroupActions.setSelectedFeedadviceApplicationId({
        id: undefined,
      }),
    );
    this.animalGroup$
      .pipe(
        skipWhile((a) => !a?.id),
        take(1),
      )
      .subscribe((animalGroup) => {
        this.dialog
          .form(FeedadviceApplicationItemDialogComponent, {
            confirmButton: true,
            confirmButtonText: this.localeService.translate('add'),
            pushHistoryState: false,
            id: null,
            productionType: animalGroup.productionType,
          })
          .subscribe((result) => {
            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              const nextId =
                animalGroup.feedAdviceApplications && animalGroup.feedAdviceApplications.length > 0
                  ? Math.max(...animalGroup.feedAdviceApplications.map((d) => d.id)) + 1
                  : 1;
              this.store.dispatch(
                animalGroupActions.addFeedadviceApplication({
                  animalGroupId: animalGroup.id,
                  feedAdviceApplication: {
                    ...result.result.feedAdviceApplication,
                    id: nextId,
                  },
                }),
              );
            }
          });
      });
  }

  editFeedAdviceApplication(feedAdviceId: number) {
    this.store.dispatch(
      animalGroupActions.setSelectedFeedadviceApplicationId({
        id: feedAdviceId,
      }),
    );
    this.animalGroup$.pipe(take(1)).subscribe((animalGroup) => {
      this.dialog
        .form(FeedadviceApplicationItemDialogComponent, {
          confirmButton: true,
          confirmButtonText: this.localeService.translate('save'),
          pushHistoryState: false,
          id: feedAdviceId,
          productionType: animalGroup.productionType,
        })
        .subscribe((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.store.dispatch(
              animalGroupActions.editFeedadviceApplication({
                animalGroupId: animalGroup.id,
                feedAdviceApplication: result.result.feedAdviceApplication,
              }),
            );
          }
          this.store.dispatch(
            animalGroupActions.setSelectedFeedadviceApplicationId({
              id: undefined,
            }),
          );
        });
    });
  }

  delete(event: Event, feedAdviceApplication: FeedAdviceApplication) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.animalGroup$.pipe(take(1)).subscribe((animalGroup) => {
      if (feedAdviceApplication && feedAdviceApplication.feedAdviceId) {
        this.store.dispatch(
          animalGroupActions.deleteFeedadviceApplicationWithConfirmation({
            animalGroupId: animalGroup.id,
            id: feedAdviceApplication.id,
          }),
        );
      }
    });
  }
}
